// Notion-style emoji picker for icon fields. Wraps the framework-agnostic
// emoji-mart Picker (the React wrapper package pins React <=18) in a popover:
// trigger button shows the current icon; picker offers search + categories +
// skin tones; extra actions: random and remove. Lazy-loaded so the emoji data
// chunk is only fetched when a picker first opens.

import { useEffect, useRef, useState } from "react";
import { useTheme } from "../hooks/useTheme";

export function EmojiPickerField({
  value,
  onChange,
}: {
  value: string;
  onChange: (icon: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const mount = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!open || !mount.current) return;
    let disposed = false;
    const node = mount.current;

    Promise.all([import("emoji-mart"), import("@emoji-mart/data")]).then(
      ([{ Picker }, data]) => {
        if (disposed) return;
        node.replaceChildren();
        new Picker({
          parent: node,
          data: data.default,
          theme,
          previewPosition: "none",
          skinTonePosition: "search",
          autoFocus: true,
          onEmojiSelect: (emoji: { native?: string }) => {
            if (emoji.native) onChange(emoji.native);
            setOpen(false);
          },
        });
      },
    );
    return () => {
      disposed = true;
      node.replaceChildren();
    };
  }, [open, theme, onChange]);

  // Close on click outside / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function random() {
    const data = (await import("@emoji-mart/data")).default as {
      emojis: Record<string, { skins: { native: string }[] }>;
    };
    const keys = Object.keys(data.emojis);
    const pick = data.emojis[keys[Math.floor(Math.random() * keys.length)]];
    if (pick?.skins?.[0]?.native) onChange(pick.skins[0].native);
  }

  return (
    <div className="emoji-field" ref={wrapper}>
      <button type="button" className="btn emoji-trigger" onClick={() => setOpen(!open)}>
        {value ? <span className="emoji-current">{value}</span> : <span className="muted">Pick icon</span>}
      </button>
      {open && (
        <div className="emoji-popover">
          <div className="emoji-popover-actions">
            <button type="button" className="btn btn-sm" onClick={random} title="Random icon">
              🎲 Random
            </button>
            <div className="spacer" />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              disabled={!value}
            >
              Remove
            </button>
          </div>
          <div ref={mount} />
        </div>
      )}
    </div>
  );
}
